/**
 * Terminal URL configuration
 * Allows configuring API base URL and Bridge (host) URL per device.
 *
 * Values are stored in localStorage and fall back to environment defaults.
 */

const STORAGE_KEY = 'photobooth_terminal_urls';

export interface TerminalUrlsConfig {
  apiBaseUrl: string | null;
  bridgeBaseUrl: string | null;
}

const defaults: TerminalUrlsConfig = {
  apiBaseUrl: null,
  bridgeBaseUrl: null,
};

function safeGetItem(key: string): string | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return null;
    }
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSetItem(key: string, value: string): void {
  try {
    if (typeof window === 'undefined' || !window.localStorage) {
      return;
    }
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore storage errors
  }
}

function parseStored(raw: string | null): TerminalUrlsConfig {
  if (!raw) return { ...defaults };
  try {
    const parsed = JSON.parse(raw) as Partial<TerminalUrlsConfig>;
    return {
      apiBaseUrl:
        typeof parsed.apiBaseUrl === 'string' ? parsed.apiBaseUrl : null,
      bridgeBaseUrl:
        typeof parsed.bridgeBaseUrl === 'string' ? parsed.bridgeBaseUrl : null,
    };
  } catch {
    return { ...defaults };
  }
}

export function getTerminalUrlsConfig(): TerminalUrlsConfig {
  const stored = safeGetItem(STORAGE_KEY);
  return parseStored(stored);
}

export function setTerminalUrlsConfig(
  updates: Partial<TerminalUrlsConfig>
): void {
  const current = getTerminalUrlsConfig();
  const next: TerminalUrlsConfig = {
    apiBaseUrl:
      updates.apiBaseUrl !== undefined
        ? updates.apiBaseUrl
        : current.apiBaseUrl,
    bridgeBaseUrl:
      updates.bridgeBaseUrl !== undefined
        ? updates.bridgeBaseUrl
        : current.bridgeBaseUrl,
  };
  safeSetItem(STORAGE_KEY, JSON.stringify(next));
}

function normaliseBaseUrl(base: string): string {
  const trimmed = base.trim();
  if (!trimmed) return trimmed;
  return trimmed.replace(/\/+$/, '');
}

/**
 * Effective API base URL for this terminal.
 * Prefers local config, falls back to VITE_API_URL or http://localhost:3001.
 */
export function getEffectiveApiBaseUrl(): string {
  const cfg = getTerminalUrlsConfig();
  const fallback =
    (import.meta.env.VITE_API_URL as string | undefined) ||
    'http://localhost:3001';
  const base = cfg.apiBaseUrl || fallback;
  return normaliseBaseUrl(base);
}

/**
 * Effective Bridge (host) base URL for this terminal.
 * Prefers local config, falls back to VITE_BRIDGE_URL or http://localhost:3001.
 */
export function getEffectiveBridgeBaseUrl(): string {
  const cfg = getTerminalUrlsConfig();
  const fallback =
    (import.meta.env.VITE_BRIDGE_URL as string | undefined) ||
    'http://localhost:3001';
  const base = cfg.bridgeBaseUrl || fallback;
  return normaliseBaseUrl(base);
}

